import { cloneDeep } from 'lodash';
import { debounceTime, Observable, of, Subject, switchMap } from 'rxjs';
import { takeUntil,take } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  Chart,
  IAxisLabelRenderEventArgs,
  ILegendClickEventArgs,
  ILoadedEventArgs,
  IMouseEventArgs,
  IPointRenderEventArgs,
  ITooltipRenderEventArgs,
  SeriesModel,
} from "@syncfusion/ej2-angular-charts";
import { LogService } from "src/app/services/log.service";
import { UtilsService } from "src/app/services/utils.service";
import { ITapChartConfig } from "../tap-chart.component";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { TapPopOutChartComponent } from "../tap-pop-out-chart/tap-pop-out-chart.component";
import { TapPopOutCompareChartComponent } from '../tap-pop-out-compare-chart/tap-pop-out-compare-chart.component';
import { CHART_COLORS } from 'src/app/shared/Constants/sharedConstants';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { DashboardWidgetService } from '@services/dashboard-widget.service';
import { selectWidgetQuery } from '@state/selectors/tank-profile.selectors';

@Component({
  selector: "app-tap-dashboard-chart",
  templateUrl: "./tap-dashboard-chart.component.html",
  styleUrls: ["./tap-dashboard-chart.component.css"],
})
export class TapDashboardChartComponent {
  @Input() chartId: string = 'chart' + this.getRandomInt();

  destroy$: Subject<boolean> = new Subject();
  private _chartLegend: TemplateRef<any>;
  public get chartLegend(): TemplateRef<any> {
    return this._chartLegend;
  }
  @Input() public set chartLegend(value: TemplateRef<any>) {
    this._chartLegend = value;
  }

  @Input() chartTitle: string;
  @Input() popOutWidth: string;
  @Input() popOutHeight: string;
  @Input() fullHeightChartConfig: ITapChartConfig;
  @Input() popOutChartHeight: string;
  @Input() popOutLegendPosition: string;
  @Input() showSpinner$: Observable<boolean> = of(false);

  //two-way binding for selectedDataIndexes
  @Input() public selectedDataIndexes: any[] = [];

  @Input()
  public set chartTooltip(value: Object | null) {
    if(!value) return;
    this.setupChartTooltip(value);
  }
  public get chartTooltip(): Object | null {
    return this._chartTooltip;
  }

  @Input()
  public set chartConfig(value: ITapChartConfig) {
    this.setupChartConfig(value);
  }
  public get chartConfig() {
    return this._chartConfig;
  }

  @Input() isCompareEnabled: boolean;
  @Input() showCompareIcon : boolean;


  @Output() chartLoaded = new EventEmitter();
  @Output() axisLabelRender: EventEmitter<IAxisLabelRenderEventArgs> =
    new EventEmitter<IAxisLabelRenderEventArgs>();
  @Output() pointRender: EventEmitter<IPointRenderEventArgs> = new EventEmitter<IPointRenderEventArgs>();
  @Output() tooltipRender: EventEmitter<ITooltipRenderEventArgs> = new EventEmitter<ITooltipRenderEventArgs>();

  private readonly dottedDashArray = "1, 2";
  private readonly chartTheme = "Material";
  private chartApi: Chart;
  private backgroundColor = "#ffffff00";
  public compareChartData = [];

  public fontFamily = this.utilsService.getSiteWideThemeFontFamily();
  public legendOrientation: string;

  public labelStyle = {
    fontFamily: this.fontFamily,
    fontWeight: "Bold",
  };

  private _chartTooltip: Object | null = null;
  private _chartConfig: ITapChartConfig = {
    chartSeries: [],
    primaryXAxis: {
      majorGridLines: {
        dashArray: this.dottedDashArray,
      },
      labelStyle: this.labelStyle,
    },
    primaryYAxis: {
      majorGridLines: {
        dashArray: this.dottedDashArray,
      },
      labelStyle: this.labelStyle,
    },
    titleStyle: {
      fontFamily: this.fontFamily,
      fontWeight: "Bolder",
      size: "18",
    },
    subTitleStyle: {
      fontFamily: this.fontFamily,
      fontWeight: "Bolder",
      opacity: 0.5,
    },
    chartArea: {
      border: {
        width: 0,
      },
      background: this.backgroundColor,
    },
    tooltip: true,
    tooltipSettings: {
      textStyle: this.labelStyle,
    },
    legendSettings: {
      visible: true,
      position: "Top",
      textStyle: this.labelStyle,
    },
    chartTheme: this.chartTheme,
    chartHeight: "350px",
    backgroundColor: this.backgroundColor,
  };
  public chartConfigSm: ITapChartConfig = null;

  public _chartData = [];
  public get chartData() {
    return this._chartData;
  }
  @Input() public set chartData(value) {
    this._chartData = value;

    if (!this.chartApi || !value) return;

    //this.chartApi.refresh();
  }

  private commonSeriesConfig: SeriesModel = {
    animation: {
      enable: true, //disable animation on series selection
      duration: 250, //milliseconds of animation
      delay: 0,
    },
  };

  /* String Resource | Value Binding */
  constructor(
    public utilsService: UtilsService,
    protected logger: LogService,
    private matDialog: MatDialog,
    private store: Store<AppState>,
    private dashboardWidgetService: DashboardWidgetService,
  ) {}

  //Setup additional chart features.
  public chartLoad(args: ILoadedEventArgs): void {
    //chartLoad event can happen more than once (refreshes or updates to chart config trigger this)
    if (this.chartApi) return;

    this.chartApi = args.chart;

    //setup tooltip options. This is left to typescript code since we have two tooltip directives.
    // one is for Syncfusion charting to define congiguration and the other is to setup a tooltip pop-up material themed
    //component
    this.setupChartTooltip();
  }

  public pieChartLoad(args: ILoadedEventArgs): void {
    this.chartApi = args.chart;
    this.setupChartTooltip();
  }

  public onChartLoaded(args: any): void {
    this.chartLoaded.emit(args);
  }

  public onChartClick(args: IMouseEventArgs) {
    this.logger.trackEvent(
      this._chartConfig.chartTitle,
      'onChartClick',
      args.name,
    );
  }

  public onLegendClick(arg: ILegendClickEventArgs) {
    this.logger.trackEvent(
      this._chartConfig.chartTitle,
      'onLegendClick',
      arg.name,
    );
  }

  public onTooltipRender(arg: ITooltipRenderEventArgs) {
    this.tooltipRender.emit(arg);
  }

  public refreshChart(): void {
    this.chartApi.refresh();
  }

  public onPopOutButtonClick() {
    this.showPopOut();
  }

  public onAxisLabelRender(arg: IAxisLabelRenderEventArgs) {
    //do nothing, stub code.
    this.axisLabelRender.emit(arg);
  }

  public onPointRender(arg: IPointRenderEventArgs) {
    //do nothing, stub code.
    this.pointRender.emit(arg);
  }

  onPointClickCallback: (args) => void | null;

  matDialogHnd: any;
  showPopOut() {
    this.onPointClickCallback = this.chartConfig.onPointClickCallback;
    const chartPopOutHeight = this.popOutChartHeight ?? this.chartConfig.chartHeight;
    const dialogOptions: MatDialogConfig = {
      data: {
        chartData: this.chartData,
        chartConfig: {
          ...cloneDeep(this.chartConfig),
          ...this.fullHeightChartConfig,
          chartHeight: chartPopOutHeight,
          onPointClickCallback: this.closePopOutThenExecutePointClickCallback.bind(this),
          primaryYAxis: {
            ...this.chartConfig.primaryYAxis,
            stripLines: [
              ...(this.chartConfig.primaryYAxis?.stripLines || []),
            ]
          }
        },
        chartLegend: this.chartLegend,
        onAxisLabelRenderCallback: this.onAxisLabelRender.bind(this),
        onPointRenderCallback: this.onPointRender.bind(this),
        onTooltipRenderCallback: this.onTooltipRender.bind(this),
        popOutLegendPosition: this.popOutLegendPosition,
      },
    };
    if (this.popOutWidth) {
      dialogOptions.width = this.popOutWidth;
      dialogOptions.minWidth = this.popOutWidth;
      dialogOptions.maxWidth = this.popOutWidth;
      dialogOptions.height = this.popOutHeight;
    }
    this.matDialogHnd = this.matDialog.open(TapPopOutChartComponent, dialogOptions);
  };

  getRandomInt() {
    const min = Math.ceil(1);
    const max = Math.floor(6544646);
    return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
  }

  private setupChartTooltip(value: ITapChartConfig = null) {
    if (!value) {
      this._chartTooltip = value;
    }
    this.chartApi.tooltip.enable = this.chartConfig.tooltip ?? true;
    this.chartApi.tooltip.textStyle = this.labelStyle;
  }

  private setupChartConfig(value: ITapChartConfig) {
    this.logger.log(`[tap-dashboard-chart] setupChartConfig called with ${value?.chartSeries?.length} series`);
    if (value?.chartType !== "Pie" &&
      value?.chartSeries &&
      value?.chartSeries.length > 0) {
      let chartConfigs = [];
      const baseConfig = cloneDeep(this.commonSeriesConfig);
      value.chartSeries.forEach((ct) => {
        chartConfigs.push({ ...baseConfig, ...ct });
      });
      value.chartSeries = chartConfigs;
    }

    this._chartConfig = { ...this._chartConfig, ...value };
    if (this._chartConfig.chartTheme.toLowerCase().includes("dark")) {
      this._chartConfig.backgroundColor =
        this.utilsService.getSiteWideThemeDarkColorHEXValue();
      this._chartConfig.chartArea.background =
        this.utilsService.getSiteWideThemeDarkColorHEXValue();
    }

    this.chartConfigSm = { ...this.chartConfig, chartTitle: null };

    if (this.chartApi && this._chartData) {
      this.chartApi.refresh();
    }
  }

  private closePopOutThenExecutePointClickCallback($event) {
    if (this.matDialogHnd) {
      this.matDialogHnd.afterClosed().subscribe(() => {
        this.onPointClickCallback($event);
      });
      this.matDialogHnd.close();
    } else {
      this.onPointClickCallback($event);
    }
  }

public onCompareButtonClick() {
    this.fetchData();

  }

showComparePopOut() {

    this.onPointClickCallback = this.chartConfig.onPointClickCallback;
    //const chartPopOutHeight = this.popOutChartHeight ?? this.chartConfig.chartHeight;
    const chartPopOutHeight = "70%";

       // Step 1: Get unique filter values
      const filterValues = [...new Set(
        this.compareChartData.map(x => x.filterValue)
      )];

      const values = this.compareChartData.map(x => x.averageDeliveryEfficiency);

      const padding = 0.02;
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);

      // Always pull the strip lines (target line) from fullHeightChartConfig which always has them set
      const stripLines = this.fullHeightChartConfig?.primaryYAxis?.stripLines
        || this.chartConfig.primaryYAxis?.stripLines
        || [];

      // Expand the axis range to always include the target so the strip line is visible
      const targetValue: number = stripLines.length > 0 ? (stripLines[0] as any).start : null;
      const axisMin = Math.max(0, Math.min(minValue, targetValue ?? minValue) - padding);
      const axisMax = Math.max(maxValue, targetValue ?? maxValue) + padding;

      // Build the series for the dialog config without mutating the parent's chartConfig
      const dialogChartSeries = filterValues.map((filter, index) => {
        const color = CHART_COLORS[index % CHART_COLORS.length];
        const filteredData = this.compareChartData
          .filter(x => x.filterValue === filter)
          .map(x => ({ ...x, date: new Date(x.date) }))
          .sort((a, b) => a.date.getTime() - b.date.getTime());

        return {
          dataSource: filteredData,
          xName: 'date',
          yName: 'averageDeliveryEfficiency',
          name: filter,
          type: 'Line',
          width: 2,
          fill: color,
          marker: {
            visible: true,
            shape: 'Circle',
            width: 10,
            height: 10,
            fill: color
          }
        };
      });

      const chartConfig = cloneDeep(this.fullHeightChartConfig ?? this.chartConfig);


    const dialogOptions: MatDialogConfig = {
      data: {
        chartData: cloneDeep(this.compareChartData),

        chartConfig: {
          ...cloneDeep(this.chartConfig),
          chartSeries: dialogChartSeries,
          chartConfig,
          chartHeight: chartPopOutHeight,
          disableLegendToggle : true,
          onPointClickCallback: this.closePopOutThenExecutePointClickCallback.bind(this),
          primaryXAxis: {
            valueType: 'DateTime',
            labelFormat: 'MMM yyyy',
            intervalType: 'Months',
            rangePadding: 'Additional'
          },
          primaryYAxis: {
            ...this.chartConfig.primaryYAxis,
            minimum: axisMin,
            maximum : axisMax,
            rangePadding: 'Additional',
            stripLines: [...stripLines]
          }
        },
        chartLegend: this.chartLegend,
        onAxisLabelRenderCallback: this.onAxisLabelRender.bind(this),
        onPointRenderCallback: (_arg) => {},
        onTooltipRenderCallback: this.onTooltipRender.bind(this),
        popOutLegendPosition: this.popOutLegendPosition,
      },
    };
        if (this.popOutWidth) {
      dialogOptions.width = this.popOutWidth;
      dialogOptions.minWidth = this.popOutWidth;
      dialogOptions.maxWidth = this.popOutWidth;
      dialogOptions.height = this.popOutHeight;
    }

    this.matDialogHnd = this.matDialog.open(TapPopOutCompareChartComponent,dialogOptions);
  };


  fetchData(){
    this.store
          .select(selectWidgetQuery)
          .pipe(
            take(1),
            switchMap((query) => {
            const filters = query?.filterModel;

            if (filters) {
            const keys = Object.keys(filters).filter(k => k !== 'active');
          }
              //this.showSpinner$.next(true);
              return this.dashboardWidgetService.getCompareHistoricalDeliveryEfficiencies(
                query
              );
            })
          )
          .subscribe((data) => {
            this.compareChartData = data.dataPoints;
            this.showComparePopOut();
          });
      }
}

